import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getAnimeById, getNewsForAnime, getRelatedAnime } from "@/utils/content";

export default function AnimeDetail() {
  const { id } = useParams();
  const anime = getAnimeById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: anime ? `${anime.title} - 58动漫详情介绍` : "作品详情 - 58动漫",
    description: anime
      ? `${anime.title}（${anime.year}年·${anime.region}）简介与剧情介绍：${anime.synopsis}类型涵盖${anime.types.join("、")}。`
      : "58动漫作品详情页，展示动画作品简介、剧情、角色与相关资讯。",
    keywords: anime
      ? `${anime.title},${anime.types.join(",")},${anime.region}动漫,${anime.year}动漫,58动漫`
      : "动漫详情,58动漫",
  });

  useEffect(() => {
    setToast("");
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!anime) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8 xl:px-12">
        <EmptyState
          icon="ri-file-unknow-line"
          title="该动漫作品暂无相关内容"
          description="你访问的作品可能已被移除或链接有误，去看看其他作品吧。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/category/最新"
            className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回动漫分类
          </Link>
        </div>
      </div>
    );
  }

  const relatedNews = getNewsForAnime(anime, 3);
  const recommendations = getRelatedAnime(anime, 4);
  const favorited = isFavorite(anime.id);

  const infoItems = [
    { label: "导演", value: anime.director, icon: "ri-user-star-line" },
    { label: "类型", value: anime.types.join(" / "), icon: "ri-price-tag-3-line" },
    { label: "年份", value: anime.year, icon: "ri-calendar-line" },
    { label: "地区", value: anime.region, icon: "ri-earth-line" },
    { label: "集数", value: `共 ${anime.episodes} 集`, icon: "ri-list-check" },
    { label: "状态", value: anime.status, icon: "ri-loader-4-line" },
  ];

  const handleFavorite = () => {
    const added = toggleFavorite(anime.id);
    setToast(added ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8 xl:px-12">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "动漫分类", to: "/category/最新" },
          { label: anime.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-6">
        <div className="flex flex-col gap-8 lg:col-span-2">
          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <div className="flex flex-col gap-5 sm:flex-row">
              <div className="w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:w-[200px] md:w-[240px]">
                <div className="aspect-[3/4] w-full">
                  <img
                    src={anime.cover}
                    alt={anime.title}
                    title={`${anime.title} - 58动漫作品封面`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                    {anime.region}动画
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      anime.status === "连载中"
                        ? "bg-accent-100 text-accent-800"
                        : "bg-secondary-100 text-secondary-900"
                    }`}
                  >
                    {anime.status}
                  </span>
                </div>

                <h1 className="mt-3 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-3xl">
                  {anime.title}
                </h1>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  {typeof anime.rating === "number" && (
                    <span className="flex items-center gap-1.5 rounded-md bg-primary-50 px-3 py-1.5">
                      <i className="ri-star-fill text-primary-500" />
                      <span className="font-heading text-lg font-extrabold text-primary-600">
                        {anime.rating.toFixed(1)}
                      </span>
                      <span className="text-xs text-foreground-500">/ 10</span>
                    </span>
                  )}
                  <span className="text-sm text-foreground-500">
                    更新至 {anime.episodes} 集 · {anime.year} 年
                  </span>
                </div>

                <p className="mt-4 text-sm leading-relaxed text-foreground-600">{anime.synopsis}</p>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                      favorited
                        ? "border border-primary-300 bg-primary-50 text-primary-600"
                        : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    <i className={`text-base ${favorited ? "ri-heart-fill" : "ri-heart-line"}`} />
                    {favorited ? "已收藏" : "加入收藏"}
                  </button>
                  <span className="flex items-center gap-1.5 rounded-md border border-dashed border-background-300 px-4 py-2.5 text-xs text-foreground-500">
                    <i className="ri-play-circle-line text-base text-foreground-400" />
                    在线观看功能演示中，暂未开放
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section aria-label="作品基本信息">
            <h2 className="mb-4 font-heading text-lg font-extrabold text-foreground-950">作品信息</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {infoItems.map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className={`${item.icon} text-sm text-primary-500`} />
                    {item.label}
                  </div>
                  <p className="mt-1.5 truncate text-sm font-medium text-foreground-900">{item.value}</p>
                </div>
              ))}
            </div>
          </section>

          <section aria-label="剧情简介">
            <h2 className="mb-4 font-heading text-lg font-extrabold text-foreground-950">剧情简介</h2>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <p className="text-sm leading-loose text-foreground-700">{anime.story}</p>
            </div>
          </section>

          {anime.characters.length > 0 && (
            <section aria-label="角色介绍">
              <h2 className="mb-4 font-heading text-lg font-extrabold text-foreground-950">角色介绍</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {anime.characters.map((character, index) => (
                  <div
                    key={character.name}
                    className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <span
                      className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-bold ${
                        index % 2 === 0
                          ? "bg-primary-100 text-primary-600"
                          : "bg-accent-100 text-accent-700"
                      }`}
                    >
                      {character.name.charAt(0)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-foreground-950">{character.name}</p>
                      <p className="mt-0.5 truncate text-xs text-foreground-500">{character.role}</p>
                      <p className="mt-0.5 truncate text-xs text-foreground-500">CV：{character.cv}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {relatedNews.length > 0 && (
            <section aria-label="相关资讯">
              <h2 className="mb-4 font-heading text-lg font-extrabold text-foreground-950">相关资讯</h2>
              <div className="flex flex-col gap-3">
                {relatedNews.map((news) => (
                  <NewsCard key={news.id} news={news} variant="compact" />
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="flex flex-col gap-6 lg:col-span-1">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-extrabold text-foreground-950">我的收藏</h2>
            <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
              收藏状态保存在你的浏览器本地，刷新后依然有效（演示功能）。
            </p>
            <button
              type="button"
              onClick={handleFavorite}
              className={`mt-4 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                favorited
                  ? "border border-primary-300 bg-primary-50 text-primary-600"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              <i className={`text-base ${favorited ? "ri-heart-fill" : "ri-heart-line"}`} />
              {favorited ? "已收藏本作品" : "加入收藏"}
            </button>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 font-heading text-base font-extrabold text-foreground-950">推荐作品</h2>
            <div className="flex flex-col gap-3">
              {recommendations.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-primary-50/60"
                >
                  <span className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 58动漫推荐作品`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </span>
                    <span className="truncate text-xs text-foreground-500">
                      {item.year} · {item.types.slice(0, 2).join(" / ")}
                    </span>
                    {typeof item.rating === "number" && (
                      <span className="flex items-center gap-1 text-xs font-bold text-primary-600">
                        <i className="ri-star-fill text-[11px]" />
                        {item.rating.toFixed(1)}
                      </span>
                    )}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <div className="rounded-lg bg-accent-50 p-5">
            <p className="flex items-center gap-2 text-sm font-bold text-accent-800">
              <i className="ri-lightbulb-line text-base" />
              发现更多
            </p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              想看同类型的作品？试试按题材筛选，或在搜索框里输入关键词。
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to={`/search?q=${encodeURIComponent(anime.types[0] || anime.title)}`}
                className="whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                浏览同类型
              </Link>
              <Link
                to="/news"
                className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-700"
              >
                查看资讯
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {toast && (
        <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}